/**
 * Widget V2 — Home (intent-first + discovery)
 *
 * Single front door. A personal hero (greeting + next booking) tops the page
 * for signed-in users, followed by a fixed set of category cards (Court
 * Rentals, Private Lessons, Group Classes, one card per standalone class category
 * such as Events, Packages) that route into booking; any
 * remaining categories (programs, custom categories, vouchers) hide behind an
 * "Other categories" expander. Signed-in users also get the preference-matched
 * "Your next session" feed. Rails surface coaches and locations, and a link
 * opens the full services menu.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { CATEGORY_ROUTE, DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";
import RecommendationsSection from "/imports/ui/components/recommendations/RecommendationsSection";
import IntentCard from "./components/IntentCard";
import HomeHero from "./components/HomeHero";
import LocationsRail from "./components/LocationsRail";
import CoachesRail from "./components/CoachesRail";

// The standard categories. Anything outside this set is treated as a custom
// category and surfaced under the "Other categories" expander.
const KNOWN_TYPES = new Set([
  "category-service-court",
  "category-service-private",
  "category-service-class",
  "category-service-term",
  "category-service-package",
  "category-service-membership"
]);

// Categories listed on the dedicated services menu page (ServicesView). Used
// only to decide whether to show the "Browse all services" link.
const MENU_TYPES = ["category-service-private", "category-service-court", "category-service-class", "category-service-term"];

function HomeView() {
  const navigate = useNavigate();
  const { slug, services, vouchers, trainers, locations } = useWidget();
  const { isAuthenticated, activeClientId } = useAuth();
  const [showOther, setShowOther] = useState(false);

  const go = (path) => navigate(`/${slug}/${path}`);

  const has = useMemo(() => {
    const types = new Set((services || []).filter((s) => !s.isStandaloneClassCategory).map((s) => s.categoryType));
    return {
      court: types.has("category-service-court"),
      lesson: types.has("category-service-private"),
      class: types.has("category-service-class"),
      term: types.has("category-service-term"),
      plan: types.has("category-service-package") || types.has("category-service-membership"),
      voucher: (vouchers || []).length > 0
    };
  }, [services, vouchers]);

  // Class categories beyond Group Classes (Events, Camps…) each get their own
  // top-level card, in the club's category order.
  const standaloneClassCategories = useMemo(() => {
    const map = new Map();
    for (const s of services || []) {
      if (s.isStandaloneClassCategory && !map.has(s.categoryId)) map.set(s.categoryId, s.categoryName || "Events");
    }
    return Array.from(map.entries());
  }, [services]);

  // Fixed, ordered top-level cards — each shown only when populated.
  const mainIntents = useMemo(() => {
    const list = [];
    if (has.court)
      list.push({ key: "courts", icon: SportsTennisIcon, title: "Court Rentals", blurb: "Reserve a court at your preferred time.", path: "courts" });
    if (has.lesson)
      list.push({
        key: "lessons",
        icon: SupervisorAccountOutlinedIcon,
        title: "Private Lessons",
        blurb: "One-on-one coaching tailored to your level.",
        path: "lessons"
      });
    if (has.class)
      list.push({
        key: "classes",
        icon: Diversity3OutlinedIcon,
        title: "Group Classes",
        blurb: "Group sessions led by our coaches for all ages and levels.",
        path: "classes"
      });
    for (const [categoryId, name] of standaloneClassCategories)
      list.push({
        key: `class-category-${categoryId}`,
        icon: EventOutlinedIcon,
        title: name,
        blurb: "Upcoming dates you can sign up for.",
        path: `events/${categoryId}`
      });
    if (has.plan)
      list.push({
        key: "plans",
        icon: LocalActivityOutlinedIcon,
        title: "Packages",
        blurb: "Stock up on sessions or unlock member pricing.",
        path: "plans"
      });
    return list;
  }, [has, standaloneClassCategories]);

  // Custom categories: one card per category name, routed to its booking view
  // when a mapping exists, else to the services menu.
  const customGroups = useMemo(() => {
    const map = new Map();
    for (const s of services || []) {
      if (KNOWN_TYPES.has(s.categoryType)) continue;
      const key = s.categoryName || s.categoryType || "Other";
      if (!map.has(key)) map.set(key, s);
    }
    return Array.from(map.entries());
  }, [services]);

  // Everything that isn't a top-level card — programs, custom categories,
  // vouchers — collapsed behind the "Other categories" toggle.
  const otherIntents = useMemo(() => {
    const list = [];
    if (has.term)
      list.push({
        key: "terms",
        icon: CalendarMonthIcon,
        title: "Programs",
        blurb: "Structured multi-week programs to build your skills.",
        path: "terms"
      });
    for (const [name, sample] of customGroups) {
      list.push({
        key: `custom-${name}`,
        icon: CategoryOutlinedIcon,
        title: name,
        blurb: "Explore this category.",
        path: CATEGORY_ROUTE[sample.categoryType] || "services"
      });
    }
    if (has.voucher)
      list.push({
        key: "vouchers",
        icon: CardGiftcardOutlinedIcon,
        title: "Vouchers",
        blurb: "Gift a voucher to redeem against bookings.",
        path: "vouchers"
      });
    return list;
  }, [has, customGroups]);

  const activeCoaches = (trainers || []).filter((t) => t.isActive !== false);
  const allLocations = locations || [];
  const intentCols = mainIntents.length >= 4 ? 3 : mainIntents.length === 3 ? 4 : 6;
  const hasMenuServices = (services || []).some((s) => MENU_TYPES.includes(s.categoryType));
  const isEmpty = mainIntents.length === 0 && otherIntents.length === 0 && !hasMenuServices;

  return (
    <Box>
      {/* Personal hero: greeting + next-booking strip + notifications ask (signed-in users only) */}
      <HomeHero go={go} />

      {/* Fixed category cards */}
      {mainIntents.length > 0 && (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {mainIntents.map((intent) => (
              <Grid item xs={6} sm={6} md={intentCols} key={intent.key}>
                <IntentCard icon={intent.icon} title={intent.title} blurb={intent.blurb} onClick={() => go(intent.path)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Custom / secondary categories, hidden behind an expander */}
      {otherIntents.length > 0 && (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Button
            onClick={() => setShowOther((v) => !v)}
            endIcon={showOther ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            {showOther ? "Hide other categories" : "Other categories"}
          </Button>
          <Collapse in={showOther}>
            <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mt: 0 }}>
              {otherIntents.map((intent) => (
                <Grid item xs={6} sm={6} md={4} key={intent.key}>
                  <IntentCard icon={intent.icon} title={intent.title} blurb={intent.blurb} onClick={() => go(intent.path)} />
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </Box>
      )}

      {/* Link to the full services menu (the inline menu lives on its own page) */}
      {hasMenuServices && (
        <Box
          sx={{
            ...DISCOVERY_CARD_SX,
            overflow: "hidden",
            mb: { xs: 4, md: 6 },
            borderColor: "primary.main",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.06)
          }}
        >
          <CardActionArea onClick={() => go("services")} sx={{ p: { xs: 1.75, md: 2.25 }, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: "2.75rem",
                height: "2.75rem",
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // The accent, like the category tiles above it.
                bgcolor: "secondary.main",
                color: "secondary.contrastText"
              }}
            >
              <FormatListBulletedIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700}>Browse all services</Typography>
              <Typography variant="body2" color="text.secondary">
                See everything you can book here.
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: "primary.main", flexShrink: 0 }} />
          </CardActionArea>
        </Box>
      )}

      {/* One-tap conversion feed — preference-matched openings for signed-in
          users. Lives here (not the dashboard) because home is where booking
          intent starts; the dashboard stays a task surface. */}
      {isAuthenticated && (
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <RecommendationsSection
            reloadKey={activeClientId || "self"}
            onEditPreferences={() => go(`dashboard?tab=preferences&from=${encodeURIComponent(`/${slug}`)}`)}
          />
        </Box>
      )}

      {allLocations.length > 0 && <LocationsRail locations={allLocations} go={go} />}

      {activeCoaches.length > 0 && <CoachesRail coaches={activeCoaches} go={go} />}

      {isEmpty && (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary">No booking options are currently available.</Typography>
        </Box>
      )}
    </Box>
  );
}

export default HomeView;
